import type { ComponentProps, FC } from "react"
import { useNavigate } from "@tanstack/react-router"

import type { Request } from "@productlane/api/src/lib/support-portal/request.types"
import { Badge, Empty, ImportantIcon, RequestsCard } from "@productlane/ui"

import EmptyIssue from "@/assets/empty-states/empty-issue.png"
import { useDetailDrawer } from "@/lib/detail-drawer"
import { getProjectIcon } from "@/lib/projects"

type Requests = ComponentProps<typeof RequestsCard>["requests"]

const isImportant = (request: Request) =>
	request.type === "feedback"
		? request.item.painLevel === "HIGH"
		: request.category === "important"

// Read-only twin of the portal's priority control, which is a toggle there.
const renderPriority = (request: Request) =>
	isImportant(request) ? (
		<div className="flex h-7 w-[7.5rem] items-center justify-end">
			<Badge variant="important">
				<ImportantIcon className="h-2.5 w-2.5 shrink-0 text-red-500" />
				<span className="whitespace-nowrap">Important</span>
			</Badge>
		</div>
	) : null

export const PortalRequestsPreview: FC<{
	requests: Requests
	isLoading: boolean
	isDueDateEnabled: boolean
	emptyText: string
	userEmail?: string
}> = ({ requests, isLoading, isDueDateEnabled, emptyText, userEmail }) => {
	const navigate = useNavigate()
	const { open: openDetail } = useDetailDrawer()

	if (isLoading) {
		return null
	}

	if (requests.length === 0) {
		return (
			<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
				<Empty
					title="No requests yet"
					text={emptyText}
					className="max-w-[20rem]"
					variant="left"
					image={<img src={EmptyIssue} alt="No requests" />}
				/>
			</div>
		)
	}

	return (
		<RequestsCard
			requests={requests}
			userEmail={userEmail}
			getProjectIcon={getProjectIcon}
			isDueDateEnabled={isDueDateEnabled}
			renderPriority={renderPriority}
			showPointer={false}
			onRequestClick={(request) => {
				if (request.type === "issue" || request.type === "project") {
					openDetail(request.type, request.item.id)
				} else if (request.feedbackId) {
					void navigate({
						to: "/threads/$id",
						params: { id: request.feedbackId },
					})
				}
			}}
		/>
	)
}
